import { useRef, useState, type ChangeEvent, type DragEvent, type ReactElement } from "react";
import type { EntryMode } from "../_stubData.ts";
import { uploadSlip } from "../repairApi.ts";
import { getBrowserClient } from "../../../supabase/browser.ts";
import { generatePath, validateFile } from "../../../slips/slipUploadHelpers.ts";
import { SrIcon } from "./SrIcon.tsx";
import { HINT_PRESETS, Stepper } from "./Stepper.tsx";

export interface RepairFormFile {
  name: string;
  size: number;
  type: string;
  preview: string | null;
  isPdf: boolean;
}

export interface RepairFormState {
  file: RepairFormFile | null;
  instructions: string;
  hints: string[];
  entryMode: EntryMode;
  // SR-TIERS — 4-tier model (was: "sharp" | "safer" | "both").
  runMode: "bold" | "moderate" | "safer" | "conservative";
  locks: string[];
}

interface Props {
  formState: RepairFormState;
  setFormState: (updater: (s: RepairFormState) => RepairFormState) => void;
  onContinue: (kind: "demo" | "real") => void;
  // SR-WIRE — when the live upload succeeds, the parent navigates to
  // ?session=<id> and advances the state machine. Optional so the
  // DEV-mode stub flow still works when no handler is provided.
  onUploaded?: (sessionId: string) => void;
}

export function UploadScreen({ formState, setFormState, onContinue, onUploaded }: Props): ReactElement {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [parsing, setParsing] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [stagedFile, setStagedFile] = useState<File | null>(null);
  // SR-REAL-STATUS — track the live extraction result so we can stop
  // hard-coding "7 legs at 92% confidence" once the upload completes.
  const [extractedCount, setExtractedCount] = useState<number | null>(null);
  const file = formState.file;

  const handleFile = (f: File | undefined): void => {
    if (!f) return;
    const isImg = f.type.startsWith("image/");
    const isPdf = f.type === "application/pdf" || f.name.toLowerCase().endsWith(".pdf");
    if (!isImg && !isPdf) {
      alert("Please drop a PNG, JPG, WEBP or PDF slip.");
      return;
    }
    const preview = isImg ? URL.createObjectURL(f) : null;
    setFormState((s) => ({
      ...s,
      file: { name: f.name, size: f.size, type: f.type, preview, isPdf },
    }));
    setStagedFile(f);
    setUploadError(null);
    // SR-WIRE — `parsing` keeps the UI affordance ("Reading your slip…")
    // for ~1.8s. The real backend extract happens when the user clicks
    // Continue (handleContinueReal), not on drop.
    setParsing(true);
    setTimeout(() => setParsing(false), 1800);
  };

  const handleContinueReal = async (): Promise<void> => {
    if (!stagedFile || !onUploaded) {
      // No live handler wired — fall back to legacy stub path.
      onContinue("real");
      return;
    }
    setUploading(true);
    setUploadError(null);
    try {
      // Storage-first upload path. The base64-in-JSON approach blows past
      // Supabase Edge's 6MB request-body limit for typical betting-slip
      // screenshots; same pattern as slip-extract + slip-autopsy uploads.
      const reject = validateFile(stagedFile);
      if (reject) throw new Error(reject);

      const supabase = getBrowserClient();
      const { data: session } = await supabase.auth.getSession();
      const userId = session.session?.user.id;
      if (!userId) throw new Error("Sign in to upload a slip.");

      const path = generatePath(userId, stagedFile.type);
      const { error: storageErr } = await supabase.storage
        .from("slip-images")
        .upload(path, stagedFile, {
          contentType: stagedFile.type,
          cacheControl: "3600",
          upsert: false,
        });
      if (storageErr) throw new Error("Upload to storage failed. Please try again.");

      const entryMode: "pre_placement" | "post_build" =
        formState.entryMode === "post-build" ? "post_build" : "pre_placement";
      const trimmedInstr = formState.instructions.trim();
      const result = await uploadSlip({
        slip_image_path: path,
        slip_image_mime: stagedFile.type,
        slip_image_size_bytes: stagedFile.size,
        entry_mode: entryMode,
        ...(trimmedInstr.length > 0 ? { user_instructions: trimmedInstr } : {}),
        ...(formState.hints.length > 0 ? { user_hints: formState.hints } : {}),
      });
      setExtractedCount(result.leg_count);
      onUploaded(result.session_id);
    } catch (e) {
      setUploadError(e instanceof Error ? e.message : String(e));
    } finally {
      setUploading(false);
    }
  };

  const onDrop = (e: DragEvent<HTMLDivElement>): void => {
    e.preventDefault();
    setDragOver(false);
    handleFile(e.dataTransfer.files?.[0]);
  };

  const onPick = (e: ChangeEvent<HTMLInputElement>): void => handleFile(e.target.files?.[0]);

  const clearFile = (): void => {
    if (file?.preview) URL.revokeObjectURL(file.preview);
    setFormState((s) => ({ ...s, file: null }));
    setStagedFile(null);
    setUploadError(null);
    if (inputRef.current) inputRef.current.value = "";
  };

  const toggleHint = (id: string): void => {
    setFormState((s) => {
      const hints = new Set(s.hints);
      if (hints.has(id)) hints.delete(id);
      else hints.add(id);
      return { ...s, hints: Array.from(hints) };
    });
  };

  const setInstructions = (v: string): void =>
    setFormState((s) => ({ ...s, instructions: v }));

  const canContinue = !!file && !parsing && !uploading;

  return (
    <div className="sr-upload-page">
      <Stepper active="upload" />

      <div className="sr-upload-head">
        <div className="sr-upload-kicker">
          <span className="pip" />
          Slip Intelligence · Repair
        </div>
        <h1>
          {formState.entryMode === "post-build"
            ? "Before you confirm. Get a second opinion."
            : "Drop your slip. Tell us anything we should know."}
        </h1>
        <p>
          {formState.entryMode === "post-build"
            ? "You've built your slip on the bookmaker — upload a screenshot before you stake. The repairer scans every leg for void risk and weak edge, then shows you what to change before you confirm."
            : "Upload from any bookmaker and add notes — what to leave alone, what to be aggressive on. The repairer reads both before it touches a single leg."}
        </p>

        <div style={{ display: "flex", justifyContent: "center", marginTop: 18 }}>
          <div className="sr-entry-mode">
            <button
              className={formState.entryMode !== "post-build" ? "on" : ""}
              onClick={() => setFormState((s) => ({ ...s, entryMode: "pre-placement" }))}
            >
              <span className="ic">
                <SrIcon name="wrench" size={12} />
              </span>
              <span className="stack">
                <span className="top">I'm building a new slip</span>
                <span className="sub">Pre-placement — construct and repair</span>
              </span>
            </button>
            <button
              className={formState.entryMode === "post-build" ? "on" : ""}
              onClick={() => setFormState((s) => ({ ...s, entryMode: "post-build" }))}
            >
              <span className="ic">
                <SrIcon name="shield" size={12} />
              </span>
              <span className="stack">
                <span className="top">I've built mine — second opinion</span>
                <span className="sub">Post-build — scan before you confirm</span>
              </span>
            </button>
          </div>
        </div>
      </div>

      <div className="sr-upload-grid">
        {!file ? (
          <div
            className={`sr-dropzone ${dragOver ? "drag" : ""}`}
            onDragOver={(e) => {
              e.preventDefault();
              setDragOver(true);
            }}
            onDragLeave={() => setDragOver(false)}
            onDrop={onDrop}
            onClick={() => inputRef.current?.click()}
            style={{ cursor: "pointer" }}
          >
            <div className="icon-wrap">
              <SrIcon name="upload" size={26} />
            </div>
            <h3>Drop your slip here</h3>
            <p>Drag and drop, or click anywhere in this box to choose a file.</p>
            <div className="formats">
              <span>PNG</span>
              <span>JPG</span>
              <span>WEBP</span>
              <span>PDF</span>
            </div>
            <div onClick={(e) => e.stopPropagation()}>
              <button className="btn primary" onClick={() => inputRef.current?.click()}>
                <SrIcon name="upload" size={13} />
                Choose file
              </button>
              <input
                ref={inputRef}
                className="sr-file-input"
                type="file"
                accept="image/png,image/jpeg,image/webp,application/pdf"
                onChange={onPick}
              />
            </div>
            <div className="or">— or —</div>
            <div className="extractor-pipe">
              <SrIcon name="extract" size={13} />
              Already extracted?{" "}
              <a
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onContinue("demo");
                }}
              >
                Use the sample SportyBet slip
              </a>
            </div>
          </div>
        ) : (
          <div className="sr-file-preview">
            <div className="sr-file-thumb">
              {file.preview ? (
                <img src={file.preview} alt={file.name} />
              ) : (
                <div className="ph">
                  <div className="pdf-glyph">PDF</div>
                  <span>{file.isPdf ? "PDF · A4 slip" : "preview unavailable"}</span>
                </div>
              )}
            </div>
            <div className="sr-file-meta">
              <div className="top">
                <div className="icname">
                  <SrIcon name="file" size={14} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className="name">{file.name}</div>
                  <div className="sub">
                    {(file.size / 1024).toFixed(1)} KB · {file.type || (file.isPdf ? "application/pdf" : "image")}
                  </div>
                </div>
                <button className="clear" onClick={clearFile} title="Remove file">
                  <SrIcon name="x" size={12} />
                </button>
              </div>
              <div className={`sr-file-status ${parsing ? "parsing" : ""}`}>
                {parsing ? (
                  <>
                    <SrIcon name="extract" size={12} />
                    <span>
                      <span className="strong">Reading your slip…</span> Running OCR across the bookmaker template.
                    </span>
                    <div className="bar">
                      <div className="fill" />
                    </div>
                  </>
                ) : (
                  <>
                    <SrIcon name="check" size={12} />
                    <span>
                      {extractedCount != null ? (
                        <>
                          <span className="strong">Ready.</span> {extractedCount} leg
                          {extractedCount === 1 ? "" : "s"} extracted. Review them in the next step.
                        </>
                      ) : (
                        <>
                          <span className="strong">Ready.</span> We&apos;ll extract every leg when you continue. Review them in the next step.
                        </>
                      )}
                    </span>
                  </>
                )}
              </div>
            </div>
          </div>
        )}

        <div className="sr-instr">
          <div className="sr-instr-head">
            <div className="ic">
              <SrIcon name="info" size={14} />
            </div>
            <div>
              <h3>Tell the repairer what to do</h3>
              <div className="sub">
                Optional — anything written here is read before the engine touches a leg.
              </div>
            </div>
          </div>

          <textarea
            placeholder={
              'e.g. "Keep my Hearts of Oak pick — it\'s a derby and I want it. Be aggressive on void risk for everything else. I prefer probability over edge."'
            }
            value={formState.instructions}
            onChange={(e) => setInstructions(e.target.value)}
            maxLength={500}
          />

          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span className="sr-chip-row-label">House rules</span>
            <span className="charcount">{formState.instructions.length}/500</span>
          </div>

          <div className="sr-chips">
            {HINT_PRESETS.map((h) => {
              const on = formState.hints.includes(h.id);
              return (
                <button
                  key={h.id}
                  className={`sr-chip ${on ? "on" : ""}`}
                  onClick={() => toggleHint(h.id)}
                  title={h.desc}
                >
                  <span className="box">{on && <SrIcon name="check" size={9} />}</span>
                  {h.label}
                </button>
              );
            })}
          </div>

          <div
            style={{
              marginTop: "auto",
              fontSize: 11.5,
              color: "var(--text-tertiary)",
              lineHeight: 1.5,
              paddingTop: 8,
              borderTop: "1px solid var(--border-faint)",
            }}
          >
            <SrIcon name="info" size={11} /> Strikza never places bets. Your slip data and instructions are used
            only to compute the repair and are deleted after 30 days.
          </div>
        </div>
      </div>

      {uploadError && (
        <div
          role="alert"
          style={{
            margin: "12px 0",
            padding: "10px 14px",
            border: "1px solid var(--neg, #d62828)",
            color: "var(--neg, #d62828)",
            borderRadius: 6,
            fontSize: 12.5,
          }}
        >
          <strong>Upload failed.</strong> {uploadError}
        </div>
      )}

      <div className="sr-step-foot">
        <div className="note">
          <strong style={{ color: "var(--text)", fontWeight: 600 }}>Step 1 of 4</strong> — drop your slip and add
          any guardrails. The repairer is selective by default: a leg retained means we agree with your judgement,
          a leg replaced means we found something measurably better.
        </div>
        <div className="actions">
          <button className="btn" onClick={() => onContinue("demo")}>
            <SrIcon name="sparkle" size={12} />
            Try sample slip
          </button>
          <button
            className="btn primary"
            disabled={!canContinue}
            onClick={() => {
              void handleContinueReal();
            }}
          >
            {uploading ? "Uploading…" : "Continue to confirm legs"}
            <SrIcon name="arrow" size={12} />
          </button>
        </div>
      </div>
    </div>
  );
}
